import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  tenancyCreatorService, 
  OnboardingFormData, 
  ProgressUpdate, 
  TenancyCreationResult 
} from '@/services/tenancyCreatorService';

/**
 * Tenancy creation states
 */
export type TenancyCreationState = 
  | 'idle'           // Not started
  | 'processing'     // Creating tenancy
  | 'success'        // Successfully created
  | 'error';         // Failed

/**
 * Hook for managing tenancy creation after onboarding completion
 */
export function useTenancyCreation() {
  const { user } = useAuth();
  const [state, setState] = useState<TenancyCreationState>('idle');
  const [progress, setProgress] = useState<ProgressUpdate[]>([]);
  const [currentProgress, setCurrentProgress] = useState<ProgressUpdate | null>(null);
  const [result, setResult] = useState<TenancyCreationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [customerID, setCustomerID] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Start polling for tenancy creation status
   */
  const startPolling = useCallback((customerID: string) => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Poll every 500ms for real-time updates
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const statusResult = await tenancyCreatorService.getTenancyStatus(customerID);
        
        if (statusResult.progress && statusResult.progress.length > 0) {
          setProgress(statusResult.progress);
          const latestProgress = statusResult.progress[statusResult.progress.length - 1];
          setCurrentProgress(latestProgress);
          
          // Check if we've reached 100% completion
          if (latestProgress.progress === 100) {
            setState('success');
            setResult(statusResult);
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            return;
          }
        }

        // Only stop polling for explicit errors, not for success flags
        // The success=true flag just indicates the API call was successful, not that tenancy creation is complete
        if (statusResult.error && statusResult.error !== 'PENDING_START') {
          // Handle the case where tenancy creation failed
          setState('error');
          
          // Special case: If it's a companyPlan error, don't show the error message to the user
          if (statusResult.error.includes('companyPlan: Required')) {
            setError(null); // Don't show error message, just show retry button
          } else {
            setError(statusResult.error);
          }
          
          setResult(statusResult);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
        // Continue polling in all other cases until we reach 100%
      } catch (error) {
        console.error('Failed to poll tenancy status:', error);
      }
    }, 500);
  }, []);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  /**
   * Create tenancy from onboarding form data
   */
  const createTenancy = useCallback(async (formData: OnboardingFormData) => {
    if (!user?.uid) {
      setError('User not authenticated');
      setState('error');
      return;
    }

    // Validate form data first
    const validation = tenancyCreatorService.validateFormData(formData);
    if (!validation.valid) {
      setError(`Validation failed: ${validation.errors.join(', ')}`);
      setState('error');
      return;
    }

    setState('processing');
    setError(null);
    setProgress([]);
    setCurrentProgress(null);

    try {
      // Start the tenancy creation process
      const result = await tenancyCreatorService.createTenancy(
        formData,
        user.uid,
        (progressUpdate) => {
          setProgress(prev => [...prev, progressUpdate]);
          setCurrentProgress(progressUpdate);
        }
      );

      // If we get a customerID back, start polling for status updates
      if (result.customerID) {
        setCustomerID(result.customerID);
        startPolling(result.customerID);
      } else {
        // If no customerID, handle as immediate result
        setResult(result);
        setState(result.success ? 'success' : 'error');
        
        if (!result.success && result.error) {
          setError(result.error);
        }
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
      setState('error');
    }
  }, [user, startPolling]);

  /**
   * Check tenancy status
   */
  const checkTenancyStatus = useCallback(async (customerID: string) => {
    try {
      const statusResult = await tenancyCreatorService.getTenancyStatus(customerID);
      setResult(statusResult);
      return statusResult;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to check status';
      setError(errorMessage);
      return null;
    }
  }, []);

  /**
   * Reset the hook state
   */
  const reset = useCallback(() => {
    stopPolling();
    setState('idle');
    setProgress([]);
    setCurrentProgress(null);
    setResult(null);
    setError(null);
    setCustomerID(null);
  }, [stopPolling]);

  /**
   * Get current progress percentage
   */
  const getProgressPercentage = useCallback(() => {
    if (currentProgress) {
      return currentProgress.progress;
    }
    if (progress.length > 0) {
      return progress[progress.length - 1].progress;
    }
    return 0;
  }, [currentProgress, progress]);

  /**
   * Check if tenancy creation is in progress
   */
  const isProcessing = state === 'processing';

  /**
   * Check if tenancy creation was successful
   */
  const isSuccess = state === 'success';

  /**
   * Check if tenancy creation failed
   */
  const isError = state === 'error';

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    // State
    state,
    isProcessing,
    isSuccess,
    isError,
    
    // Data
    progress,
    currentProgress,
    result,
    error,
    
    // Computed values
    progressPercentage: getProgressPercentage(),
    customerID: customerID || result?.customerID,
    
    // Actions
    createTenancy,
    checkTenancyStatus,
    reset,
    startPolling,
    stopPolling
  };
}
